import { useState } from "react";

import { ITileCacheSeeder } from "../Features/MapComponents/TileCache/types";
import { DistanceUnits, HeadingUnit } from "../Features/Common/Units/types";
import { IConfig, IMapSettings, ISettingOption, ISettings, ITileCacheSettings, IUISettings, MapLocationTrackingBehaviour, ToolbarFlowOrientation } from "../Settings/settingsTypes";
import { defaultSettings } from "../Settings/defaults";
import { copySettings, normaliseSettings } from "../Settings/utils";
import { ISettingsService } from "../Settings/serviceTypes";


const useLocalStorageSettingsService = (config: IConfig, storeName: string, cacheSeeders: ITileCacheSeeder[]): ISettingsService => {

    const tileCacheSeederOptions: ISettingOption<string>[] = cacheSeeders.filter(cs => cs.isSupported() === true).map(cs => { return { id: cs.id, name: cs.name }; });

    const toolbarFlowOrientationOptions: ISettingOption<ToolbarFlowOrientation>[] = [
        { id: ToolbarFlowOrientation.RightHanded, name: 'Right Handed' },
        { id: ToolbarFlowOrientation.LeftHanded, name: 'Left Handed' }
    ];

    const headingUnitOptions: ISettingOption<HeadingUnit>[] = [
        { id: HeadingUnit.Degrees, name: 'Degrees from North' },
        { id: HeadingUnit.Cardinal, name: 'Cardinal (N,E,S,W)' }
    ];

    const distanceUnitOptions: ISettingOption<DistanceUnits>[] = [
        { id: DistanceUnits.Metric, name: 'Metric' },
        { id: DistanceUnits.Imperial, name: 'Imperial' }
    ];

    const mapLocationTrackingBehaviourOptions: ISettingOption<MapLocationTrackingBehaviour>[] = [
        { id: MapLocationTrackingBehaviour.Always, name: 'Always' },
        { id: MapLocationTrackingBehaviour.TurnOffOnPan, name: 'Turn off when manually panning' }
    ];


    const getFromStorage = (): ISettings | null => {
        const rawItem = localStorage.getItem(storeName);
        return !!rawItem ? JSON.parse(rawItem) : null;
    };

    const saveToStorage = (data: ISettings) => {
        localStorage.setItem(storeName, JSON.stringify(data));
    };

    const currentSettings = normaliseSettings(getFromStorage() ?? copySettings(defaultSettings), defaultSettings, cacheSeeders);
    const [settings, setSettings] = useState<ISettings>(currentSettings);

    const setMap = (s: IMapSettings) => {
        const merged = { ...settings, map: s };
        setSettings(merged);
    };

    const setTileCache = (s: ITileCacheSettings) => {
        const merged = { ...settings, tileCache: s };
        setSettings(merged);
    };

    const setUI = (s: IUISettings) => {
        const merged = { ...settings, ui: s };
        setSettings(merged);
    };

    const save = () => {
        saveToStorage(settings);
    };

    return {
        save,
        config,
        settings,
        tileCacheSeederOptions, toolbarFlowOrientationOptions, headingUnitOptions, distanceUnitOptions, mapLocationTrackingBehaviourOptions,
        setMap, setTileCache, setUI
    };
    
};

export default useLocalStorageSettingsService;